import { useRouter } from 'next/router';
import { gql } from '@apollo/client';
import { useMemo } from 'react';
import { 
  Header,
  Footer,
  Main,
  Container,
  SEO,
  Breadcrumb,
  SchemaMarkup
} from '../../components';
import EntryHeaderPartner from '../../components/EntryHeaderPartner/EntryHeaderPartner';
import PartnerContent from '../../components/PartnerContent/PartnerContent';
import classNames from 'classnames/bind';
import styles from './[slug].module.scss';
import * as MENUS from '../../constants/menus';
import { PartnerContentFragment } from '../../fragments/partner/PartnerContentFragment';
import { PartnerSEOFragment } from '../../fragments/partner/PartnerSEOFragment';
import { createSchema, createWebPageSchema, createBreadcrumbSchema } from '../../lib/schema';
import { buildHreflangAlternates } from '../../lib/polylang';

const cx = classNames.bind(styles);

export async function getStaticPaths() {
  // Pre-generate the known partner pages (slugs must match WordPress and app links, e.g. FrontPageHero)
  const partnerSlugs = [
    'google-partner-2',
    'meta',
    'make-com',
    'wp-engine',
    'worldline',
    'bambora',
    'cookieinformation'
  ];

  const paths = partnerSlugs.map((slug) => ({
    params: { slug },
  }));

  return {
    paths,
    fallback: 'blocking' // Generate other pages on-demand
  };
}

export async function getStaticProps({ params }) {
  const { slug } = params;
  
  const { getApolloClient } = await import('@faustwp/core');
  const apolloClient = getApolloClient();
  
  try {
    const [partnerResult, sharedResult] = await Promise.all([
      // Fetch partner data
      apolloClient.query({
        query: gql`
          ${PartnerContentFragment}
          ${PartnerSEOFragment}
          query GetPartnerByUri($uri: String!) {
            nodeByUri(uri: $uri) {
              ... on Partner {
                id
                title
                slug
                uri
                modified
                featuredImage {
                  node {
                    mediaItemUrl
                    altText
                  }
                }
                ...PartnerContentFragment
                ...PartnerSEOFragment
              }
            }
          }
        `,
        variables: { uri: `/partnere/${slug}` }
      }),
      
      // Fetch shared data
      apolloClient.query({
        query: gql`
          query GetSharedDataForPartner($headerLocation: MenuLocationEnum!, $footerLocation: MenuLocationEnum!) {
            generalSettings {
              title
              description
            }
            headerMenuItems: menuItems(first: 100, where: { location: $headerLocation }) {
              nodes {
                id
                label
                path
                parentId
                cssClasses
                menu {
                  node {
                    name
                  }
                }
              }
            }
            footerMenuItems: menuItems(first: 100, where: { location: $footerLocation }) {
              nodes {
                id
                label
                path
                parentId
                cssClasses
                menu {
                  node {
                    name
                  }
                }
              }
            }
            mediaboosterSettings {
              mediaboosterSettingsFields {
                logo {
                  node {
                    mediaItemUrl
                    altText
                  }
                }
                logoIcon {
                  node {
                    mediaItemUrl
                    altText
                  }
                }
                ctaSeksjonMotebooking {
                  heading
                  subtext
                }
              }
            }
          }
        `,
        variables: {
          headerLocation: MENUS.PRIMARY_LOCATION,
          footerLocation: MENUS.FOOTER_LOCATION,
        },
      }),
    ]);

    const partner = partnerResult.data?.nodeByUri;
    const sharedData = sharedResult.data;

    // Return 404 if partner is not found
    if (!partner) {
      return {
        notFound: true,
        revalidate: 60, // Retry quickly in case data propagation is delayed
      };
    }

    return {
      props: {
        partner,
        sharedData,
      },
      revalidate: 300, // 5 minutes
    };
  } catch (error) {
    console.error('Error fetching partner data:', error);
    return {
      notFound: true,
      revalidate: 60, // Prevent transient upstream errors from sticking as 404
    };
  }
}

export default function Partner({ partner, sharedData }) {
  const router = useRouter();
  
  const { title, featuredImage, seo, partnere } = partner;
  const { title: siteTitle, description: siteDescription } = sharedData?.generalSettings ?? {};
  const primaryMenu = sharedData?.headerMenuItems?.nodes ?? [];
  const footerMenu = sharedData?.footerMenuItems?.nodes ?? [];
  const lightLogo = sharedData?.mediaboosterSettings?.mediaboosterSettingsFields?.logo?.node;

  // SEO values 
  const pageTitle = seo?.title || title || 'Partner';
  const pageDescription = seo?.description || `${title} - Partner hos Mediabooster`;
  const pageImage = seo?.openGraph?.image?.secureUrl || seo?.openGraph?.image?.url || featuredImage?.node?.mediaItemUrl || 'https://mediaboosterhe.wpenginepowered.com/wp-content/uploads/2025/01/tjenester-mediabooster.png';
  
  // Canonical URL
  const canonicalUrl = seo?.canonicalUrl || (router.asPath === '/' 
    ? (process.env.NEXT_PUBLIC_SITE_URL || 'https://mediabooster.no') 
    : `${process.env.NEXT_PUBLIC_SITE_URL || 'https://mediabooster.no'}${router.asPath}`);

  // Build breadcrumb items
  const breadcrumbItems = [
    { label: 'Hjem', path: '/' },
    { label: 'Partnere', path: '/partnere' },
    { label: title, path: router.asPath }
  ];

  // Generate schema for single partner page
  const pageSchema = useMemo(() => {
    return createSchema([
      createWebPageSchema({
        title: pageTitle,
        description: pageDescription,
        url: partner?.uri,
        imageUrl: pageImage
      }),
      createBreadcrumbSchema(breadcrumbItems)
    ]);
  }, [pageTitle, pageDescription, partner?.uri, pageImage, breadcrumbItems]);

  return (
    <>
      <SchemaMarkup schema={pageSchema} />
      <SEO 
        locale="nb_NO"
        hreflangAlternates={buildHreflangAlternates(partner)}
        seo={{
          title: pageTitle,
          description: pageDescription,
          canonicalUrl: canonicalUrl,
          robots: seo?.robots,
          openGraph: {
            title: pageTitle,
            description: pageDescription,
            url: canonicalUrl,
            siteName: siteTitle || 'Mediabooster',
            type: 'article',
            image: {
              url: pageImage,
              secureUrl: pageImage,
              width: 1200,
              height: 630,
              alt: `${title} - Partner hos Mediabooster`
            }
          },
          twitter: {
            card: 'summary_large_image',
            site: '@mediabooster',
            title: pageTitle,
            description: pageDescription,
            image: pageImage
          }
        }}
      />
      
      <Header
        title={siteTitle}
        description={siteDescription}
        menuItems={primaryMenu}
        lightLogo={lightLogo}
        sharedData={sharedData}
      />
      
      <Main>
        <Container className={cx('containerNoPadding')}>
          <div className={cx('container')}>
            <div className={cx('partnerHeader')}>
              <EntryHeaderPartner
                header={partner?.partnere?.layout?.find(layout => layout.fieldGroupName === 'PartnereLayoutEntryHeaderLayout')?.header || partner?.title}
                longDescription={partner?.partnere?.layout?.find(layout => layout.fieldGroupName === 'PartnereLayoutEntryHeaderLayout')?.description}
                logoLight={partner?.partnere?.logoLightmodde?.node}
                modified={partner?.modified}
                breadcrumb={<Breadcrumb items={breadcrumbItems} />}
                languageCode="no"
              />
            </div>

            <PartnerContent layout={partnere?.layout} languageCode="no" />
          </div>
        </Container>
      </Main>
      
      <Footer
        title={siteTitle}
        menuItems={footerMenu}
        sharedData={sharedData}
      />
    </>
  );
} 